import { useState, useEffect, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import travelHistoryData from "src/pages/trafficPage/data/TravelHistory.json";

/**
 * Requête GraphQL pour récupérer l'historique de trajet d'un véhicule
 */
const GET_TRAVEL_HISTORY = gql`
  query GetTravelHistory($uniqueId: String!, $from: String!, $to: String!, $snapToRoad: Boolean) {
    getTravelHistory(
      uniqueId: $uniqueId
      from: $from
      to: $to
      snapToRoad: $snapToRoad
    ) {
      distanceTravelledKms
      points {
        ts
        lat
        lng
        speed
        isHA
        isHB
        address
      }
    }
  }
`;

/**
 * Hook personnalisé pour récupérer les replay de trajet
 * @param {string} uniqueId - L'IMEI unique du véhicule
 * @param {string} from - Timestamp de début (format Unix ou ISO)
 * @param {string} to - Timestamp de fin (format Unix ou ISO)
 * @param {boolean} snapToRoad - Option pour snapToRoad
 * @param {boolean} useMockData - Utiliser les données mock si l'API retourne vide
 * @returns {Object} { tripData, loading, error, refetch }
 */
const useTripReplay = ({ uniqueId, from, to, snapToRoad = false, useMockData = true }) => {
  const [tripData, setTripData] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);

  const [getTravelHistory, { loading, error, refetch }] = useLazyQuery(GET_TRAVEL_HISTORY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
  });

  // Fonction pour déclencher la récupération des données
  const fetchTripData = useCallback(async () => {
    if (!uniqueId || !from || !to) {
      setTripData(null);
      return;
    }

    try {
      const { data } = await getTravelHistory({
        variables: {
          uniqueId,
          from,
          to,
          snapToRoad,
        },
      });

      if (data?.getTravelHistory?.points && data.getTravelHistory.points.length > 0) {
        setTripData({
          distanceTravelledKms: data.getTravelHistory.distanceTravelledKms || 0,
          points: data.getTravelHistory.points,
        });
      } else if (useMockData) {
        // Fallback sur les données mock
        console.log("API retourne vide, utilisation des données mock");
        setTripData({
          distanceTravelledKms: travelHistoryData.data?.getTravelHistory?.distanceTravelledKms || 0,
          points: travelHistoryData.data?.getTravelHistory?.points || [],
        });
      } else {
        setTripData(null);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du trajet:", err);
      if (useMockData) {
        // Fallback sur les données mock en cas d'erreur
        console.log("Erreur API, utilisation des données mock");
        setTripData({
          distanceTravelledKms: travelHistoryData.data?.getTravelHistory?.distanceTravelledKms || 0,
          points: travelHistoryData.data?.getTravelHistory?.points || [],
        });
      } else {
        setTripData(null);
      }
    }
  }, [uniqueId, from, to, snapToRoad, getTravelHistory, useMockData]);

  // Déclencher la récupération quand les paramètres changent
  useEffect(() => {
    if (triggerFetch && uniqueId && from && to) {
      fetchTripData();
    }
  }, [triggerFetch, uniqueId, from, to, fetchTripData]);

  // Fonction pour déclencher manuellement la récupération
  const execute = useCallback(() => {
    setTriggerFetch(true);
  }, []);

  // Réinitialiser les données quand les paramètres changent
  useEffect(() => {
    if (!uniqueId || !from || !to) {
      setTripData(null);
    }
  }, [uniqueId, from, to]);

  return {
    tripData,
    loading,
    error,
    refetch: execute,
  };
};

/**
 * Transforme les points GPS bruts de l'API en format pour ReplayMapLite
 * @param {Array} points - Points GPS de l'API [{ ts, lat, lng, speed, isHA, isHB, address }]
 * @param {number} speedLimit - Limite de vitesse pour détecter les violations
 * @returns {Object} { positions, speedData, events, duration, distance, maxSpeed, violationsCount }
 */
export const transformTripData = (points, speedLimit = 30) => {
  if (!points || points.length === 0) {
    return null;
  }

  // Trier les points par timestamp
  const sortedPoints = [...points].sort((a, b) => parseInt(a.ts) - parseInt(b.ts));

  const firstTimestamp = parseInt(sortedPoints[0].ts);
  const lastTimestamp = parseInt(sortedPoints[sortedPoints.length - 1].ts);
  const durationSeconds = Math.max(1, lastTimestamp - firstTimestamp);
  const durationMinutes = Math.floor(durationSeconds / 60);

  // Calculer la distance totale (si disponible) ou estimer
  let totalDistance = 0;
  for (let i = 1; i < sortedPoints.length; i++) {
    const dist = haversineDistance(
      sortedPoints[i - 1].lat,
      sortedPoints[i - 1].lng,
      sortedPoints[i].lat,
      sortedPoints[i].lng
    );
    totalDistance += dist;
  }

  // Trouver la vitesse maximale
  const maxSpeed = Math.max(...sortedPoints.map((p) => parseFloat(p.speed) || 0));

  // Compter les violations
  const violationsCount = sortedPoints.filter((p) => (parseFloat(p.speed) || 0) > speedLimit).length;

  // Générer les positions pour ReplayMapLite
  const positions = sortedPoints.map((point, index) => {
    const timeOffset = parseInt(point.ts) - firstTimestamp;
    const speed = parseFloat(point.speed) || 0;
    const isViolation = speed > speedLimit;

    return {
      time: timeOffset,
      lat: parseFloat(point.lat),
      lng: parseFloat(point.lng),
      speed: Math.round(speed),
      isViolation,
      address: point.address,
      isHA: point.isHA,
      isHB: point.isHB,
    };
  });

  // Générer les données de vitesse pour le graphique
  const speedData = sortedPoints.map((point) => {
    const timeOffset = parseInt(point.ts) - firstTimestamp;
    const mins = Math.floor(timeOffset / 60);
    const secs = timeOffset % 60;
    const baseTime = new Date(firstTimestamp * 1000);
    baseTime.setSeconds(baseTime.getSeconds() + timeOffset);
    const time = `${String(baseTime.getHours()).padStart(2, "0")}:${String(baseTime.getMinutes()).padStart(2, "0")}:${String(baseTime.getSeconds()).padStart(2, "0")}`;
    
    const speed = parseFloat(point.speed) || 0;
    const isViolation = speed > speedLimit;

    return {
      time,
      speed: Math.round(speed),
      isViolation,
      limit: speedLimit,
    };
  });

  // Générer les événements marquants
  const events = generateEvents(sortedPoints, firstTimestamp, speedLimit);

  return {
    positions,
    speedData,
    events,
    duration: durationMinutes || 1,
    distance: parseFloat(totalDistance.toFixed(2)),
    maxSpeed: Math.round(maxSpeed),
    violationsCount,
  };
};

/**
 * Génère les événements marquants d'un trajet
 */
const generateEvents = (points, firstTimestamp, speedLimit) => {
  const events = [];

  if (points.length === 0) return events;

  // Événement de départ
  const startTime = new Date(firstTimestamp * 1000);
  events.push({
    time: formatTime(startTime),
    type: "start",
    label: `Départ - ${points[0].address || "Point de départ"}`,
    icon: "🟢",
  });

  // Trouver les violations
  let violationFound = false;
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const speed = parseFloat(point.speed) || 0;
    
    if (speed > speedLimit && !violationFound) {
      const eventTime = new Date(parseInt(point.ts) * 1000);
      events.push({
        time: formatTime(eventTime),
        type: "violation",
        label: `VIOLATION - ${Math.round(speed)} km/h (lim ${speedLimit})`,
        icon: "🔴",
      });
      violationFound = true;
    }

    // Accélération brusque
    if (point.isHA) {
      const eventTime = new Date(parseInt(point.ts) * 1000);
      events.push({
        time: formatTime(eventTime),
        type: "warning",
        label: `Accélération brusque détectée`,
        icon: "⚠️",
      });
    }

    // Freinage brusque
    if (point.isHB) {
      const eventTime = new Date(parseInt(point.ts) * 1000);
      events.push({
        time: formatTime(eventTime),
        type: "warning",
        label: `Freinage brusque détecté`,
        icon: "⚠️",
      });
    }
  }

  // Événement d'arrivée
  const lastPoint = points[points.length - 1];
  const endTime = new Date(parseInt(lastPoint.ts) * 1000);
  events.push({
    time: formatTime(endTime),
    type: "end",
    label: `Arrivée - ${lastPoint.address || "Point d'arrivée"}`,
    icon: "🟓",
  });

  return events;
};

/**
 * Formate une date en HH:MM:SS
 */
const formatTime = (date) => {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
};

/**
 * Calcule la distance entre deux points GPS (formule de Haversine)
 * @returns distance en km
 */
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Rayon de la terre en km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default useTripReplay;
